export enum NotificationType {
  FOLLOW = 'FOLLOW',
  ADDED_TEMPLATES = 'ADDED_TEMPLATES',
  TRENDING_TEMPLATES = 'TRENDING_TEMPLATES',
  DESIGN_SHARED = 'DESIGN_SHARED',
  DESIGN_COMMENT = 'DESIGN_COMMENT',
  BOOKMARK = 'BOOKMARK',
  LIKE = 'LIKE',
  TEMPLATE_FEATURED = 'TEMPLATE_FEATURED',
  DESIGN_UPVOTE = 'DESIGN_UPVOTE',
  COMMENT_MENTION = 'COMMENT_MENTION',
  /**
   * @deprecated use PROJECT_THREAD instead
   */
  PRESENTATION_THREAD = 'PRESENTATION_THREAD',
  /**
   * @deprecated use PROJECT_THREAD_COMMENT instead
   */
  PRESENTATION_THREAD_COMMENT = 'PRESENTATION_THREAD_COMMENT',
  PROJECT_THREAD = 'PROJECT_THREAD',
  PROJECT_THREAD_COMMENT = 'PROJECT_THREAD_COMMENT',
  COLLABORATION_INVITE = 'COLLABORATION_INVITE',
}

export interface GenericNotification<
  Type extends NotificationType,
  Data = unknown,
> {
  id: string;
  type: Type;
  data: Data;
  readAt: string | null;
  seenAt: string | null;
  createdAt: string;
  relatedUser?: {
    slug: string | null;
    name: string | null;
    profileImage: string | null;
  };
}

type FollowNotification = GenericNotification<
  NotificationType.FOLLOW,
  { message: string }
>;

export type AddedTemplatesNotification = GenericNotification<
  NotificationType.ADDED_TEMPLATES,
  { id: number; name: string; templatePreviewObjectName: string }[]
>;

export type TrendingTemplatesNotification = GenericNotification<
  NotificationType.TRENDING_TEMPLATES,
  { id: number; name: string; templatePreviewObjectName: string }[]
>;

type DesignSharedNotification = GenericNotification<
  NotificationType.DESIGN_SHARED,
  {
    path: string;
    link: string;
    objectName: string;
  }
>;

export type DesignCommentNotification = GenericNotification<
  NotificationType.DESIGN_COMMENT,
  {
    path: string;
    templatePreviewObjectName: string;
    postId: string;
    comment: string;
  }
>;

type BookmarkNotification = GenericNotification<
  NotificationType.BOOKMARK,
  {
    path: string;
    templatePreviewObjectName: string;
  }
>;

type LikeNotification = GenericNotification<
  NotificationType.LIKE,
  {
    path: string;
    templatePreviewObjectName: string;
  }
>;

type TemplateFeaturedNotification = GenericNotification<
  NotificationType.TEMPLATE_FEATURED,
  {
    path: string;
    templatePreviewObjectName: string;
  }
>;

type DesignUpvoteNotification = GenericNotification<
  NotificationType.DESIGN_UPVOTE,
  {
    path: string;
    templatePreviewObjectName: string;
  }
>;

export type CommentMentionNotification = GenericNotification<
  NotificationType.COMMENT_MENTION,
  { name: string; path: string }
>;

export type ProjectCommentNotification = GenericNotification<
  | NotificationType.PRESENTATION_THREAD
  | NotificationType.PRESENTATION_THREAD_COMMENT
  | NotificationType.PROJECT_THREAD
  | NotificationType.PROJECT_THREAD_COMMENT,
  {
    path: string;
    designName: string;
    designId: string;
    artboardId: string;
    threadId: string;
    comment: string;
    commenterUserIds: string[];
    totalComments: number;
  }
>;

export type CollaborationInviteNotificationData = GenericNotification<
  NotificationType.COLLABORATION_INVITE,
  {
    projectName: string;
    role: 'edit' | 'view';
    shareLink: string;
    shareSrc?: string;
    senderName: string | null;
    senderProfileImage: string | null;
    userLink?: string;
  }
>;

export type Notification =
  | FollowNotification
  | AddedTemplatesNotification
  | TrendingTemplatesNotification
  | DesignSharedNotification
  | DesignCommentNotification
  | BookmarkNotification
  | LikeNotification
  | TemplateFeaturedNotification
  | DesignUpvoteNotification
  | CommentMentionNotification
  | ProjectCommentNotification
  | CollaborationInviteNotificationData;
